import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  describeError,
  listSensitiveEvents,
  type SensitiveEventDto,
} from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

/**
 * Compact widget listing the last 7 days of high-sensitivity audit events
 * (design D14.2). Visible ONLY to Admins — the component itself short-circuits
 * for any other role so the API is not even called.
 */
export function SensitiveEventsWidget() {
  const role = useAuthStore((s) => s.operator?.role ?? null);
  const [items, setItems] = useState<SensitiveEventDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerItems, setDrawerItems] = useState<SensitiveEventDto[] | null>(null);

  useEffect(() => {
    if (role !== 'Admin') return;
    let cancelled = false;
    listSensitiveEvents(7)
      .then((data) => !cancelled && setItems(data.items))
      .catch((err) => !cancelled && setError(describeError(err)));
    return () => {
      cancelled = true;
    };
  }, [role]);

  async function openDrawer() {
    setDrawerOpen(true);
    try {
      const data = await listSensitiveEvents(30);
      setDrawerItems(data.items);
    } catch (err) {
      setError(describeError(err));
    }
  }

  if (role !== 'Admin') return null;

  return (
    <section
      aria-labelledby="sensitive-events-heading"
      className="card"
      style={{ marginTop: 24 }}
    >
      <h2
        id="sensitive-events-heading"
        style={{ marginTop: 0, fontSize: 16, display: 'flex', justifyContent: 'space-between' }}
      >
        Eventos sensíveis (últimos 7 dias)
        <button
          type="button"
          className="btn btn-secondary"
          style={{ fontSize: 12 }}
          onClick={openDrawer}
        >
          Ver todos os 30 dias
        </button>
      </h2>

      {error && <div className="error-banner">{error}</div>}

      {!items && !error && <div className="muted">Carregando…</div>}

      {items && items.length === 0 && (
        <div className="muted">Nenhum evento sensível nos últimos 7 dias.</div>
      )}

      {items && items.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {items.slice(0, 10).map((ev) => (
            <li
              key={ev.id}
              style={{
                padding: '8px 0',
                borderBottom: '1px solid var(--color-border)',
                fontSize: 13,
              }}
            >
              <span
                title={new Date(ev.timestamp).toISOString()}
                style={{ color: 'var(--color-text-secondary)', marginRight: 8 }}
              >
                {relativeTime(ev.timestamp)}
              </span>
              <strong>{ev.actor.fullName}</strong>{' '}
              {translateAction(ev.action)}{' '}
              {ev.target && (
                <>
                  <strong>{ev.target.fullName}</strong>
                </>
              )}
              {ev.reason && (
                <span style={{ color: 'var(--color-text-secondary)' }}> — “{truncate(ev.reason, 60)}”</span>
              )}{' '}
              <Link to={`/operators/${ev.actor.id}`}>Ver perfil →</Link>
            </li>
          ))}
        </ul>
      )}

      {drawerOpen && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'var(--color-overlay)',
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 30,
          }}
          onClick={() => setDrawerOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="sensitive-events-drawer-title"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--color-surface)',
              width: 'min(560px, 96vw)',
              padding: 24,
              overflow: 'auto',
            }}
          >
            <h2 id="sensitive-events-drawer-title" style={{ marginTop: 0 }}>
              Eventos sensíveis — últimos 30 dias
            </h2>
            {!drawerItems && <div className="muted">Carregando…</div>}
            {drawerItems && drawerItems.length === 0 && (
              <div className="muted">Nenhum evento sensível nos últimos 30 dias.</div>
            )}
            {drawerItems && (
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {drawerItems.map((ev) => (
                  <li key={ev.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--color-border)', fontSize: 13 }}>
                    <span style={{ color: 'var(--color-text-secondary)', marginRight: 8 }}>
                      {new Date(ev.timestamp).toLocaleString('pt-BR')}
                    </span>
                    <strong>{ev.actor.fullName}</strong> {translateAction(ev.action)}{' '}
                    {ev.target && <strong>{ev.target.fullName}</strong>}
                    {ev.reason && <span style={{ color: 'var(--color-text-secondary)' }}> — “{truncate(ev.reason, 100)}”</span>}
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setDrawerOpen(false)}
              style={{ marginTop: 16 }}
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `${minutes} min atrás`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h atrás`;
  const days = Math.floor(hours / 24);
  return `${days} d atrás`;
}

function translateAction(action: string): string {
  switch (action) {
    case 'Account.Revoked':
      return 'revogou';
    case 'Account.PasswordReset':
      return 'resetou senha de';
    case 'Account.MfaReset':
      return 'resetou MFA de';
    case 'Account.Updated':
      return 'atualizou';
    case 'Account.Created':
      return 'criou';
    default:
      return action;
  }
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}
